/**
 * ConsentContext — App-level cookie consent state
 *
 * Manages GDPR-compliant per-category consent via the `odg_required` JSON cookie.
 * Provides consent state, setters, and preferences panel visibility to the entire app.
 *
 * Cookie shape (odg_required):
 * {
 *   consent: { analytics: bool, functional: bool, marketing: bool } | null,
 *   session_token: string (UUID),
 *   policy_version: string,
 *   consented_at: string (ISO 8601) | null
 * }
 *
 * @epic MVP 0.1.1.2 — Cookie Consent & Preference Layer
 * @story SCRUM-1446
 */

import React, { createContext, useState, useCallback, useMemo } from 'react';

export const CONSENT_POLICY_VERSION = '1.0';

const COOKIE_NAME = 'odg_required';
const MAX_AGE = 31536000; // 1 year in seconds

/**
 * Read and parse the odg_required cookie.
 * @returns {Object|null} Parsed cookie data or null if absent/invalid.
 */
function readOdgRequired() {
  if (typeof document === 'undefined') {
    return null;
  }
  const match = document.cookie.match(/(?:^|;\s*)odg_required=([^;]*)/);
  if (!match) {
    return null;
  }
  try {
    return JSON.parse(decodeURIComponent(match[1]));
  } catch {
    return null;
  }
}

/**
 * Serialize and set the odg_required cookie.
 * @param {Object} data - Cookie data to write
 */
function writeOdgRequired(data) {
  const encoded = encodeURIComponent(JSON.stringify(data));
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${COOKIE_NAME}=${encoded}; path=/; max-age=${MAX_AGE}; SameSite=Lax${secure}`;
}

/**
 * Get existing session_token or create a new one.
 * Never regenerates if one already exists.
 * @param {string|null|undefined} existing - Existing session token
 * @returns {string} Session token (UUID)
 */
function getOrCreateSessionToken(existing) {
  return existing || crypto.randomUUID();
}

/**
 * Fire-and-forget POST to /api/v1/consent.
 * session_token is NOT included in the body — server reads it from the cookie.
 * @param {Object} categories - { analytics, functional, marketing }
 */
function fireConsentApi(categories) {
  const apiUrl = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_URL)
    ? import.meta.env.VITE_API_URL
    : '';
  fetch(`${apiUrl}/api/v1/consent`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      preference: categories,
      policy_version: CONSENT_POLICY_VERSION,
    }),
  }).catch(() => {}); // fire-and-forget: ignore errors
}

export const ConsentContext = createContext(null);

export function ConsentProvider({ children }) {
  const [cookieData, setCookieData] = useState(() => readOdgRequired());
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const consent = cookieData?.consent ?? null;
  const sessionToken = cookieData?.session_token ?? null;

  const setConsent = useCallback((categories) => {
    const existing = readOdgRequired();
    const token = getOrCreateSessionToken(existing?.session_token);

    const newData = {
      consent: categories,
      session_token: token,
      policy_version: CONSENT_POLICY_VERSION,
      consented_at: new Date().toISOString(),
    };

    writeOdgRequired(newData);
    setCookieData(newData);
    setIsPanelOpen(false);

    // Fire-and-forget API call
    fireConsentApi(categories);
  }, []);

  const acceptAll = useCallback(() => {
    setConsent({ analytics: true, functional: true, marketing: true });
  }, [setConsent]);

  const rejectAll = useCallback(() => {
    setConsent({ analytics: false, functional: false, marketing: false });
  }, [setConsent]);

  const openPreferences = useCallback(() => {
    setIsPanelOpen(true);
  }, []);

  const closePrefPanel = useCallback(() => {
    setIsPanelOpen(false);
  }, []);

  const value = useMemo(() => ({
    consent,
    sessionToken,
    policyVersion: CONSENT_POLICY_VERSION,
    setConsent,
    acceptAll,
    rejectAll,
    openPreferences,
    isPanelOpen,
    closePrefPanel,
  }), [consent, sessionToken, setConsent, acceptAll, rejectAll, openPreferences, isPanelOpen, closePrefPanel]);

  return (
    <ConsentContext.Provider value={value}>
      {children}
    </ConsentContext.Provider>
  );
}
